import {
  Box,
  IconChevron,
  Stack,
  useResponsiveValue,
} from 'braid-design-system';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useEventListener } from 'src/hooks/useEventListener';

import * as styles from './SnapCarousel.css';

interface SnapCarouselProps {
  children: ReactNode[];
  show: {
    mobile: number;
    tablet: number;
    desktop: number;
    wide: number;
  };
  label: string;
}

// When one item is shown per page it is narrowed so the next one peeks in
const PEEK_WIDTH = '85%';

// How it works:
// - The track is a normal horizontal scroll area, so swipe, trackpad and
//   keyboard scrolling come from the browser.
// - CSS scroll snapping (`scroll-snap-type` on the track, `scroll-snap-align`
//   on the first card of each page) makes scrolling always settle at the
//   start of a page.
// - Arrows and dots just scroll the track to the first card of a page.
// - On every scroll (and on resize) we read the scroll position to work out
//   the current page and whether to show the arrows.
export const SnapCarousel = ({ children, show, label }: SnapCarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activePage, setActivePage] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const responsiveValue = useResponsiveValue();
  const perPage = responsiveValue(show) ?? show.desktop;
  const pageCount = Math.ceil(children.length / perPage);

  // Distance from the first card to the first card of `page`, i.e. where that
  // page starts in the track
  const pageOffset = useCallback(
    (page: number) => {
      const items = trackRef.current?.children;
      if (!items?.length) {
        return 0;
      }
      const first = items[0] as HTMLElement;
      const target =
        items[Math.min(Math.max(page, 0) * perPage, items.length - 1)];
      return (target as HTMLElement).offsetLeft - first.offsetLeft;
    },
    [perPage],
  );

  const updatePosition = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    // 1px tolerance because browsers report fractional scroll positions
    const maxScroll = track.scrollWidth - track.clientWidth;
    const end = track.scrollLeft >= maxScroll - 1;
    const step = pageOffset(1);

    setAtStart(track.scrollLeft <= 1);
    setAtEnd(end);
    // The last page can be partly filled, so it can't scroll a full page
    // width; at the end of the track it's always the last page
    setActivePage(
      end || !step
        ? pageCount - 1
        : Math.min(Math.round(track.scrollLeft / step), pageCount - 1),
    );
  }, [pageCount, pageOffset]);

  useEffect(updatePosition, [updatePosition]);
  useEventListener('resize', updatePosition);

  const scrollToPage = (page: number) => {
    trackRef.current?.scrollTo({ left: pageOffset(page) });
  };

  const hasControls = pageCount > 1;

  let itemWidth = `calc((100% - ${perPage - 1} * ${styles.gap}) / ${perPage})`;
  if (perPage === 1) {
    itemWidth = hasControls ? PEEK_WIDTH : '100%';
  }

  return (
    <Stack space="medium">
      <Box className={styles.container}>
        {hasControls && !atStart ? (
          <Box
            component="button"
            type="button"
            aria-label="Previous"
            borderRadius="full"
            display={{ mobile: 'none', tablet: 'flex' }}
            alignItems="center"
            justifyContent="center"
            className={styles.previousArrow}
            onClick={() => scrollToPage(activePage - 1)}
          >
            <IconChevron direction="left" />
          </Box>
        ) : null}

        <div
          ref={trackRef}
          role="region"
          aria-label={label}
          tabIndex={0}
          className={styles.track}
          onScroll={updatePosition}
        >
          {children.map((child, index) => (
            <div
              key={index}
              className={
                index % perPage === 0
                  ? `${styles.item} ${styles.pageStart}`
                  : styles.item
              }
              style={{ flexBasis: itemWidth }}
            >
              {child}
            </div>
          ))}
        </div>

        {hasControls && !atEnd ? (
          <Box
            component="button"
            type="button"
            aria-label="Next"
            borderRadius="full"
            display={{ mobile: 'none', tablet: 'flex' }}
            alignItems="center"
            justifyContent="center"
            className={styles.nextArrow}
            onClick={() => scrollToPage(activePage + 1)}
          >
            <IconChevron direction="right" />
          </Box>
        ) : null}
      </Box>

      {hasControls ? (
        <Box display="flex" justifyContent="center">
          {Array.from({ length: pageCount }, (_, page) => (
            <Box
              key={page}
              component="button"
              type="button"
              aria-label={`Go to page ${page + 1}`}
              aria-current={page === activePage ? 'true' : undefined}
              display="flex"
              alignItems="center"
              justifyContent="center"
              className={styles.dotButton}
              onClick={() => scrollToPage(page)}
            >
              <Box
                borderRadius="full"
                className={[
                  styles.dot,
                  page === activePage ? styles.activeDot : undefined,
                ]}
              />
            </Box>
          ))}
        </Box>
      ) : null}
    </Stack>
  );
};
